import ApiExecutor from "./executor";
import ApiEndpoints from "../../models/static/api-endpoints";
import {
    ApiInterceptors,
    ApiResponse,
    CreateTemplateRequestDS,
    GetTemplatesApiResponseDS,
    RemoveTemplateRequestDS,
    ReplaceTemplateRequestDS
} from "./type-declarations";
import {ApiMethods} from "../../models/constants/enums";
import {QueryBuilderQueryRuleListValueEntry, QueryBuilderTemplate} from "../../../index";
import {CancelToken} from "axios";
import QueryBuilderUtils from "../utils";

/**
 * The interface that houses all the available api calls related to this package.
 */
class QueryBuilderApiService {

    /**
     * The executor of this api responsible for handling the api call themselves.
     */
    private static readonly executor = new ApiExecutor();

    private static version = '1';

    /**
     * Injects api interceptors to the axios instance used in this interface.
     * @param interceptors
     */
    static injectInterceptors(interceptors: Partial<ApiInterceptors>): void {
        return this.executor.injectInterceptors(interceptors);
    }


    // ###################################       LIST OPTIONS         ###################################

    /**
     * Fetches the filter options from the server.
     * @param fetchOptionsApiUrl    the url to fetch the filter options from.
     * @param apiKey                the apiKey to fetch the filter options for.
     * @param keyword               the keyword to filter the filter options with.
     * @param cancelToken           the cancel token to cancel the request.
     */
    static async getFilterOptions(fetchOptionsApiUrl: string, apiKey: string, keyword: string | undefined, cancelToken: CancelToken): ApiResponse<Array<QueryBuilderQueryRuleListValueEntry>> {
        return await this.executor.execute({
            method: ApiMethods.get,
            url: fetchOptionsApiUrl,
            params: {
                apiKey: apiKey,
                ...(!!keyword?.trim() && {
                    keyword: keyword.trim(),
                })
            },
            cancelToken: cancelToken,
        });
    }

    // ###################################       QUERY BUILDER         ###################################


    /**
     * Fetches the templates from the server.
     * @param applicationName          the name application to fetch the templates from.
     * @param sectionName              the name of the section to fetch the templates from.
     * @param cancelToken   the cancel token to cancel the request.
     */
    static async getTemplates(applicationName: string, sectionName: string, cancelToken: CancelToken): ApiResponse<QueryBuilderTemplate[]> {
        const response = await this.executor.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.queryBuilder.getData,
            cancelToken: cancelToken,
            params: {
                applicationName: applicationName,
            }
        });
        if (response?.resultFlag) {
            response.data = (response.data as Array<GetTemplatesApiResponseDS> ?? [])
                .filter(e => e.name === sectionName && e.version === QueryBuilderApiService.version)
                .map<QueryBuilderTemplate[]>(e => {
                    let res: Array<QueryBuilderTemplate>;
                    try {
                        res = JSON.parse(e.data as unknown as string ?? '{}');
                    } catch {
                        res = [];
                    }
                    return res;
                })
                .flat()
                .filter(e => !!e.data)
        }
        return response;
    }

    /**
     * Creates a new template in the server.
     * @param data      The data to create the template with.
     */
    static async createTemplate(data: CreateTemplateRequestDS): ApiResponse<QueryBuilderTemplate> {
        const newTemplate = {...data.newTemplate, id: QueryBuilderUtils.createUUID()};
        const response = await this.executor.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.queryBuilder.saveData,
            data: {
                applicationName: data.applicationName,
                name: data.name,
                version: QueryBuilderApiService.version,
                data: JSON.stringify([
                    ...data.templates,
                    newTemplate
                ])
            },
        });
        if (response?.resultFlag) {
            response.data = newTemplate;
        }
        return response;
    }


    /**
     * Replaces the template in the server.
     * @param data    the data to replace the template with.
     */
    static async replaceTemplate(data: ReplaceTemplateRequestDS): ApiResponse {
        return await this.executor.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.queryBuilder.saveData,
            data: {
                applicationName: data.applicationName,
                name: data.name,
                version: QueryBuilderApiService.version,
                data: JSON.stringify(data.templates.map(e => e.id === data.templateId ? {...e, data: data.data} : e))
            },
        });
    }

    /**
     * Deletes the template from the server.
     * @param data    the request data to delete the template with.
     */
    static async removeTemplate(data: RemoveTemplateRequestDS): ApiResponse {
        return await this.executor.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.queryBuilder.saveData,
            data: {
                applicationName: data.applicationName,
                name: data.name,
                version: QueryBuilderApiService.version,
                data: JSON.stringify(data.templates.filter(e => e.id !== data.templateId))
            },
        });
    }
}


export * from './type-declarations';
export default QueryBuilderApiService;
